"use client";

import { useEffect, useRef, useState } from "react";

import { api } from "@/lib/api/client";
import type { Job } from "@/lib/api/types";
import { approvalText } from "@/lib/cost-report";
import { formatEuro } from "@/lib/format";

import { StepRow } from "./step-row";
import { isFinished, useJob } from "./use-job";

type Props = {
  jobId: number;
  /** Überschrift der Ladeansicht, z. B. „Keywords werden abgerufen“. */
  title: string;
  /** Einmal, sobald der Job einen Endstatus erreicht (z. B. um das Ergebnis zu laden). */
  onFinished?: (job: Job) => void;
  /** Bei jedem neuen Stand des Jobs (Schritt fertig, Status, Kosten). Als stabile Funktion übergeben. */
  onUpdate?: (job: Job) => void;
};

function infoLine(job: Job): string {
  const done = job.steps.filter((s) => s.status === "done" || s.status === "skipped").length;
  const spent = job.cost_eur > 0 ? ` · Kosten bisher ${formatEuro(job.cost_eur)}` : "";
  // Die Schätzung steht immer dabei, wo es Euro-Kosten gibt (ahrefs-Units und Gemini-Tokens stehen im Schritt selbst)
  const estimate =
    (job.estimated_cost_eur ?? 0) > 0 ? ` · Schätzung ${formatEuro(job.estimated_cost_eur ?? 0)}` : "";
  switch (job.status) {
    case "queued":
      return `Wartet auf den Worker …${estimate}`;
    case "running":
      return `Schritt ${Math.min(done + 1, job.steps.length)} von ${job.steps.length}${spent}${estimate}`;
    case "awaiting_approval":
      return `Pausiert, wartet auf Freigabe${spent}`;
    case "succeeded":
      return `Fertig · Kosten ${formatEuro(job.cost_eur)}${estimate}`;
    case "cancelled":
      return `Abgebrochen${spent}`;
    default:
      return `Fehlgeschlagen${spent}`;
  }
}

/** „Ladeansicht mit Teilschritten“ (Mockup Briefing): Fortschritt, Schritte mit Endpoint und Ergebnis, Freigabe. */
export function JobProgress({ jobId, title, onFinished, onUpdate }: Props) {
  const { job, error, setJob } = useJob(jobId);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const notified = useRef(false);

  useEffect(() => {
    if (job && isFinished(job) && !notified.current) {
      notified.current = true;
      onFinished?.(job);
    } else if (job && !isFinished(job)) {
      notified.current = false; // nach „Wiederholen“ oder „Überspringen“ meldet der nächste Endstatus erneut
    }
  }, [job, onFinished]);

  useEffect(() => {
    if (job) onUpdate?.(job);
  }, [job, onUpdate]);

  const act = async (action: "approve" | "cancel" | "retry" | "skip") => {
    setBusy(true);
    setActionError(null);
    try {
      setJob(await api<Job>(`/api/jobs/${jobId}/${action}`, { method: "POST" }));
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    } finally {
      setBusy(false);
    }
  };

  if (!job) {
    return (
      <p className={`text-sm ${error ? "text-danger" : "text-muted"}`} role={error ? "alert" : undefined}>
        {error ?? "Job wird geladen …"}
      </p>
    );
  }

  return (
    <section
      aria-labelledby={`job-${job.id}-title`}
      aria-live="polite"
      className="flex max-w-3xl flex-col gap-4 rounded-lg border border-line bg-white p-6"
    >
      <div className="flex flex-col gap-1">
        <h2 id={`job-${job.id}-title`} className="text-lg font-semibold">
          {title}
        </h2>
        <span className="text-[13px] text-muted">{infoLine(job)}</span>
      </div>

      <div
        role="progressbar"
        aria-label="Fortschritt"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={job.progress}
        className="h-2 overflow-hidden rounded bg-[#e9ecea]"
      >
        <div className="h-full bg-accent transition-[width]" style={{ width: `${job.progress}%` }} />
      </div>

      {job.awaiting_approval && (
        <div className="flex flex-col gap-3 rounded-md bg-warning-bg px-4 py-3.5 text-sm text-warning">
          <p>{approvalText(job)}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void act("approve")}
              className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white disabled:opacity-60"
            >
              Freigeben und starten
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void act("cancel")}
              className="h-11 rounded-md border border-field bg-white px-3.5 text-sm text-ink disabled:opacity-60"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {job.status === "failed" && (
        <div className="flex flex-col gap-3 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          <p role="alert">{job.error ?? "Der Job ist fehlgeschlagen."}</p>
          {job.can_resume && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void act("retry")}
                className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white disabled:opacity-60"
              >
                Schritt wiederholen
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void act("skip")}
                className="h-11 rounded-md border border-field bg-white px-3.5 text-sm text-ink disabled:opacity-60"
              >
                Überspringen und weitermachen
              </button>
              <span className="text-xs">Erledigte Schritte laufen nicht noch einmal und kosten nichts.</span>
            </div>
          )}
        </div>
      )}
      {actionError && (
        <p role="alert" className="text-sm text-danger">
          {actionError}
        </p>
      )}
      {error && (
        <p role="status" className="text-xs text-danger">
          Aktualisierung fehlgeschlagen: {error}
        </p>
      )}

      <ol aria-label="Teilschritte">
        {job.steps.map((step) => (
          <StepRow key={step.key} step={step} />
        ))}
      </ol>
    </section>
  );
}
